import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { User, LogOut, LayoutDashboard, Menu, X, Sparkles } from 'lucide-react';
import { useState } from 'react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  if (location.pathname.startsWith('/dashboard')) return null;
  if (['/generate', '/library', '/community'].some((p) => location.pathname.startsWith(p))) return null;

  return (
    <>
      {/* Top promo banner */}
      <div className="bg-[#08080C] border-b border-white/[0.05] text-center py-2.5 text-[10px] font-bold tracking-[0.25em] uppercase" data-testid="promo-banner">
        <span className="text-[#FFD93D]">150 AI MODELS</span><span className="text-white/40 mx-2">·</span>
        <span className="text-white/60">DALL·E · FLUX · SORA · KLING</span><span className="text-white/40 mx-2">·</span>
        <span className="text-white/60">50 FREE CREDITS</span>
      </div>

      <nav className="sticky top-0 z-50 bg-[#0A0A0F]/90 backdrop-blur-xl border-b border-white/[0.05]" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          {/* Left nav links */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/generate" className="text-xs font-bold text-white/70 hover:text-[#FF6B4A] transition-colors uppercase tracking-[0.2em]" data-testid="nav-generate">Generate</Link>
            <Link to="/community" className="text-xs font-bold text-white/70 hover:text-[#FF6B4A] transition-colors uppercase tracking-[0.2em]" data-testid="nav-community">Community</Link>
            <Link to="/pricing" className="text-xs font-bold text-white/70 hover:text-[#FF6B4A] transition-colors uppercase tracking-[0.2em]" data-testid="nav-pricing">Pricing</Link>
            <Link to="/models" className="text-xs font-bold text-[#FFD93D] hover:text-[#FFE066] transition-colors uppercase tracking-[0.2em]" data-testid="nav-models">Models</Link>
          </div>

          {/* Center logo */}
          <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-baseline gap-0" data-testid="navbar-logo">
            <span className="font-body text-3xl font-extrabold tracking-tight text-white uppercase">DIS</span>
            <span className="font-body text-3xl font-extrabold tracking-tight uppercase text-[#FF6B4A]">2</span>
            <span className="font-body text-3xl font-extrabold tracking-tight text-white uppercase">ORT</span>
          </Link>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-2">
            {/* ⌘K command palette hint */}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('d2-cmdk-open'))}
              className="hidden lg:inline-flex items-center gap-2 h-9 px-3 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] hover:border-white/[0.12] transition-colors text-[11px] text-white/50 hover:text-white/80"
              data-testid="navbar-cmdk-trigger"
              aria-label="Open command palette"
            >
              <span>Search</span>
              <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-[9px] font-mono">⌘K</kbd>
            </button>
            {user ? (
              <>
                <Link to="/generate">
                  <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-semibold px-5 h-9 rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] text-xs" data-testid="nav-cta-generate">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-white/80 hover:text-[#FF6B4A] hover:bg-white/[0.04] h-9 px-2" data-testid="nav-user-menu">
                      {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <User className="w-4 h-4" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0F0F15] border-white/[0.1] text-white min-w-[180px]">
                    <DropdownMenuItem asChild className="hover:bg-white/[0.06] focus:bg-white/[0.06] cursor-pointer text-xs">
                      <Link to="/library" className="flex items-center gap-2" data-testid="nav-dropdown-library"><LayoutDashboard className="w-3.5 h-3.5" /> Library</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="hover:bg-white/[0.06] focus:bg-white/[0.06] cursor-pointer text-xs">
                      <Link to="/canvas" className="flex items-center gap-2" data-testid="nav-dropdown-canvas"><LayoutDashboard className="w-3.5 h-3.5" /> Canvas</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/[0.08]" />
                    <DropdownMenuItem onClick={logout} className="hover:bg-white/[0.06] focus:bg-white/[0.06] text-[#EF4444] cursor-pointer text-xs" data-testid="nav-dropdown-logout"><LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link to="/login">
              <Button data-testid="nav-login-btn" className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold px-6 h-9 rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] text-xs uppercase tracking-wider">Sign In</Button>
            </Link>
            )}
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-white/[0.05] bg-[#0A0A0F] px-6 py-6 space-y-4">
            <Link to="/generate" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase tracking-wider text-white/80">Generate</Link>
            <Link to="/community" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase tracking-wider text-white/80">Community</Link>
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase tracking-wider text-white/80">Pricing</Link>
            <Link to="/models" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase tracking-wider text-[#FFD93D]">All 150 Models</Link>
            {user ? (
              <>
                <Link to="/library" onClick={() => setMobileOpen(false)} className="block text-sm font-bold uppercase tracking-wider text-white/80">Library</Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="text-sm text-[#EF4444] uppercase tracking-wider font-bold">Sign Out</button>
              </>
            ) : (
              <Link to="/login">
              <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold w-full rounded-full text-xs h-10 uppercase tracking-wider">Sign In</Button>
            </Link>
            )}
          </div>
        )}
      </nav>
    </>
  );
}
